import { useLocation } from 'react-router-dom'
import { WHATSAPP_NUMBER } from '../config/siteConfig'
import { trackPixelContact } from '../utils/metaPixel'
import { logContactClick } from '../firebase'

// A fixed, always-visible way to reach a human, on every customer-facing
// page. This exists because of a specific finding from a mobile-first audit
// of the buy funnel (Home -> Shop -> Product -> Cart -> Checkout): the ONLY
// way to reach WhatsApp or a phone number anywhere on the site was to
// scroll to the very bottom of the page footer, or navigate away to
// /contact. On the checkout page specifically - the single moment someone
// is most likely to get stuck (an address that's hard to pin, a question
// about a delivery zone) - there was no help affordance at all above the
// footer. The footer numbers were also plain text, not tappable tel:/wa.me
// links, which matters a lot on a touchscreen.
//
// Message text adapts to the page so the shop owner sees context
// immediately in WhatsApp without having to ask "which product?" - that
// question is exactly the kind of back-and-forth that loses an order.
const messageForPath = (pathname) => {
  if (pathname === '/checkout') return "Hi, I'm having trouble at checkout and need some help."
  if (pathname === '/cart') return "Hi, I have a question about my cart before I order."
  if (pathname.startsWith('/product/')) return "Hi, I have a question about this product before I order."
  return "Hi, I'd like to ask about ordering from Akhtar Sabir Fresh."
}

const WhatsAppButton = () => {
  const location = useLocation()
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(messageForPath(location.pathname))}`

  // Product pages have their own fixed bar at the bottom (the sticky Add to
  // Cart bar in ProductDetail.jsx, added for the same audit this button
  // came out of) - sit above it there instead of overlapping it. The
  // homepage's sticky "Shop Fresh" bar (Home.jsx) is mobile-only and only
  // appears after scrolling past the hero, so this shift only needs to
  // apply below the sm breakpoint there.
  const isProductPage = location.pathname.startsWith('/product/')
  const isHomePage = location.pathname === '/'

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      // Fire-and-forget: the anchor's own default navigation opens WhatsApp
      // in a new tab regardless of what this handler does, so there's no
      // need to preventDefault or await anything here.
      onClick={() => {
        trackPixelContact('whatsapp')
        logContactClick('whatsapp', location.pathname)
      }}
      // Fixed bottom-right, above the mobile-safe-area inset so it never
      // sits under a phone's home-indicator bar. z-40 - one below the
      // navbar/mobile-menu's z-50, so an open nav menu still overlaps it.
      className={`fixed z-40 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-black/20 hover:scale-105 active:scale-95 transition-transform ${
        isProductPage
          ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))]'
          : isHomePage
            ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))] sm:bottom-[calc(1.25rem+env(safe-area-inset-bottom))]'
            : 'bottom-[calc(1.25rem+env(safe-area-inset-bottom))]'
      }`}
      style={{ right: '1.25rem' }}
    >
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.005c5.46 0 9.91-4.45 9.91-9.91C21.93 6.45 17.5 2 12.04 2Zm5.83 14.16c-.24.68-1.4 1.3-1.94 1.38-.5.08-1.12.11-1.81-.11-.42-.13-.95-.31-1.63-.6-2.87-1.24-4.74-4.13-4.88-4.32-.14-.19-1.17-1.56-1.17-2.97 0-1.41.74-2.1 1-2.39.26-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.41-.07.64.49.24.58.81 2 .88 2.14.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.16-.19.68-.79.87-1.06.19-.28.37-.23.63-.14.26.09 1.64.77 1.92.91.28.14.46.21.53.33.07.12.07.68-.17 1.36Z" />
      </svg>
    </a>
  )
}

export default WhatsAppButton
